/**
 * SpatialMemorySkillEffect — Astral trunk passive: departure shimmer breadcrumb.
 *
 * When unlocked, leaves a fading shimmer visual at every Phase Step departure
 * point. The shimmer is a pure cosmetic navigation breadcrumb — no mechanical
 * effect, no zone, no status. It marks where the player was.
 *
 * Implementation: registers a listener on AstralTeleportSystem.onTeleport.
 * Each event spawns a shimmer via the injected render callback. Shimmers
 * self-expire after departureShimmerDurationMs (30 seconds).
 *
 * Visual rendering is injected via onShimmerSpawn to keep this module
 * Phaser-free. The game scene supplies the actual Phaser graphics object
 * creation via that callback.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § spatial_memory
 * @see ADR-064 (attunement shared systems architecture)
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { AstralTeleportSystem, TeleportEvent } from '../../../systems/AstralTeleportSystem.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ShimmerEntry {
  id: string;
  x: number;
  y: number;
  totalMs: number;
  remainingMs: number;
}

export interface SpatialMemoryDeps {
  readonly teleport: Pick<AstralTeleportSystem, 'onTeleport'>;
  readonly renderer?: {
    /** Called when a new shimmer spawns at a departure point. */
    onShimmerSpawn(id: string, x: number, y: number, durationMs: number): void;
    /** Called when a shimmer expires. */
    onShimmerExpire(id: string): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SpatialMemoryConfig {
  readonly departureShimmerDurationMs: number;
}

const DEFAULT_CONFIG: SpatialMemoryConfig = {
  departureShimmerDurationMs: 30_000, // 30 seconds
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SpatialMemorySkillEffect implements SkillEffect {
  readonly skillId = 'spatial_memory';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: SpatialMemoryDeps;
  private readonly config: SpatialMemoryConfig;
  private readonly _shimmers: ShimmerEntry[] = [];
  private _nextId = 0;

  constructor(deps: SpatialMemoryDeps, config: Partial<SpatialMemoryConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Register the departure listener at construction (passive is always active once unlocked).
    deps.teleport.onTeleport((event: TeleportEvent) => {
      this._spawnShimmer(event.fromX, event.fromY);
    });
  }

  // ---------------------------------------------------------------------------
  // State accessors
  // ---------------------------------------------------------------------------

  /** All currently active shimmer entries. */
  get activeShimmers(): ReadonlyArray<Readonly<ShimmerEntry>> {
    return this._shimmers;
  }

  /** Count of shimmers currently displayed. */
  get shimmerCount(): number {
    return this._shimmers.length;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private _spawnShimmer(x: number, y: number): void {
    const id = `shimmer_${this._nextId++}`;
    const entry: ShimmerEntry = {
      id,
      x,
      y,
      totalMs: this.config.departureShimmerDurationMs,
      remainingMs: this.config.departureShimmerDurationMs,
    };
    this._shimmers.push(entry);
    this.deps.renderer?.onShimmerSpawn(id, x, y, this.config.departureShimmerDurationMs);
  }

  // ---------------------------------------------------------------------------
  // SkillEffect interface
  // ---------------------------------------------------------------------------

  update(delta: number): void {
    for (let i = this._shimmers.length - 1; i >= 0; i--) {
      const shimmer = this._shimmers[i];
      shimmer.remainingMs -= delta;
      if (shimmer.remainingMs <= 0) {
        this._shimmers.splice(i, 1);
        this.deps.renderer?.onShimmerExpire(shimmer.id);
      }
    }
  }

  destroy(): void {
    // Expire all shimmers immediately.
    for (const shimmer of this._shimmers) {
      this.deps.renderer?.onShimmerExpire(shimmer.id);
    }
    this._shimmers.length = 0;
  }
}
